import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { VisitDtoModelType, OrderStatus, RouteTimelineDtoModelType } from '../../../../store/mst/models';
import { useStore } from '../../../../store/mst';
import deliveryDetails from './DeliveryDetails';
import TimelineCardHeader from './TimelineCardHeader';
import { DetailsCard, DetailsCardSkeleton } from '../../../shared/DetailsCard';
import { filterRows } from './utils';

interface OrderTimelineSectionProps {
  visit: VisitDtoModelType;
}

const getHeaderDate = (
  visit: VisitDtoModelType,
  event?: RouteTimelineDtoModelType,
): string | undefined => {
  const attempt = event ? visit.attempt(event.eventUuid) : visit.attempt();

  // Prefer the actual attempt date (when the delivery was completed/missed)
  if (attempt?.formattedDateTime) {
    return attempt.formattedDateTime;
  }
  // Fall back to the scheduled start time from the timeline event (in scheduled state)
  if (event?.formattedStartDateTime) {
    return event.formattedStartDateTime;
  }
  return undefined;
};

const OrderTimelineSection: React.FC<OrderTimelineSectionProps> = ({ visit }) => {
  const { t } = useTranslation('common');
  const fetchedRef = useRef(false);

  const { model, ui } = useStore();
  const { loadingOrderTimeline, orderTimelineEvents } = model;

  const orderUuid = visit?.uuid as string;
  const projectId = ui.selectedWorkspace?.id as string;
  const isScheduled = visit?.isScheduled;
  // Resolve via the guarded getter — reading the raw scheduledEvent MSTGQLRef crashes when it
  // dangles (e.g. after a CSV upload orphans it on an already-scheduled visit, ROUT-2850).
  const routeSolutionId = visit?.resolvedScheduledEvent?.id;

  const orderTimeline = orderTimelineEvents(orderUuid);

  useEffect(() => {
    fetchedRef.current = false;

    let isMounted = true;

    const fetchData = () => {
      if (projectId && orderUuid && routeSolutionId && isScheduled && isMounted && !fetchedRef.current) {
        fetchedRef.current = true;
        model.fetchOrderTimeline(projectId, orderUuid);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [projectId, orderUuid, routeSolutionId, isScheduled, model]);

  if (loadingOrderTimeline) {
    return <DetailsCardSkeleton />;
  }

  if (!isScheduled) {
    return null;
  }

  const cards = orderTimeline?.length > 0
    ? orderTimeline.map((event) => ({
      key: event.eventUuid,
      status: visit.status(event.eventUuid),
      date: getHeaderDate(visit, event),
      hasLocationException: !!visit.attempt(event.eventUuid)?.hasLocationException,
      rows: filterRows(deliveryDetails(t, visit, event.eventUuid)),
    }))
    : [{
      key: 'order-details-card-default',
      status: OrderStatus.SCHEDULED as OrderStatus,
      date: getHeaderDate(visit),
      hasLocationException: !!visit.attempt()?.hasLocationException,
      rows: filterRows(deliveryDetails(t, visit)),
    }];

  return (
    <>
      {cards.map(({
        key, status, date, hasLocationException, rows,
      }) => (
        <DetailsCard
          key={key}
          data-testid="order-details-card"
          header={<TimelineCardHeader status={status} date={date} hasLocationException={hasLocationException} />}
          rows={rows}
        />
      ))}
    </>
  );
};

export default observer(OrderTimelineSection);
